import { DateTime } from 'luxon';
import { FC, useMemo } from 'react';
import styled from 'styled-components';

import Text from '@/components/Text';
import { BPTestingProjectResource } from '@/types/resources';
import { BPTestingProjectStatus } from '@/utils/enums';
import { Color } from '@/utils/theme';

const InfoCardBox = styled.div`
  margin-top: 16px;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  border-radius: 4px;
  padding: 24px;
  margin-bottom: 32px;
  position: relative;
`;

const InfoCardGroupsWrapper = styled.div`
  display: flex;
  flex: 1;
`;

const Dot = styled.span<{ $color: Color }>`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  margin-right: 8px;
  background: ${({ theme, $color }) => theme.colors[$color]};
`;

const InfoCardGroup = styled.div`
  width: 50%;
  grid-row-gap: 24px;
  padding-right: 24px;
  border-right: 1px solid ${({ theme }) => theme.colors.lightGray};
  &:last-child {
    border-right: 0;
  }
  ${Text} {
    &:first-child {
      margin-bottom: 2px;
    }
  }
`;

const InfoCardGroupFlexInner = styled.div`
  flex: 1;
`;

const InfoCardItem = styled.div`
  margin-bottom: 24px;
  &:last-of-type {
    margin-bottom: 0;
  }
`;

const InfoCardGroupFlex = styled(InfoCardGroup)`
  display: flex;
  padding-left: 24px;
  padding-right: 0;
  button {
    margin-left: 12px;
  }
`;

const StatusLabel = styled(Text)`
  display: flex;
  align-items: center;
`;

const NoteText = styled(Text)`
  word-break: break-all;
  a {
    text-decoration: underline;
  }
`;

interface Props {
  project: BPTestingProjectResource;
}

const InfoCard: FC<Props> = ({ project }) => {
  const statusLabel =
    project.status === BPTestingProjectStatus.ACTIVE ? 'Active' : 'Ended';

  const notes = useMemo(() => {
    if (!project.note) {
      return null;
    }

    return project.note.replace(
      /(?:(https?:\/\/[^\s]+))/m,
      '<a href="$1" target="_blank" rel="noreferrer noopener">$1</a>',
    );
  }, [project.note]);

  return (
    <InfoCardBox>
      <InfoCardGroupsWrapper>
        <InfoCardGroup>
          <InfoCardItem>
            <Text $type="smallBold" $color="gray">
              Project UUID
            </Text>
            <Text
              $type="caption"
              as="a"
              target="_blank"
              rel="noopener noreferrer"
              href={`https://adsmanager.facebook.com/adsmanager/manage/ads?act=${process.env.REACT_APP_FB_ADS_ACCOUNT}&business_id=1032398303507511&filter_set=SEARCH_BY_CAMPAIGN_GROUP_NAME-STRING%1ECONTAIN%1E%22${project.uuid}%22`}
            >
              {project.uuid}
            </Text>
          </InfoCardItem>
          <InfoCardItem>
            <Text $type="smallBold" $color="gray">
              Owner
            </Text>
            <Text $type="caption">{project.owner}</Text>
          </InfoCardItem>
          <InfoCardItem>
            <Text $type="smallBold" $color="gray">
              Note
            </Text>
            <NoteText
              $type="caption"
              dangerouslySetInnerHTML={{ __html: notes ?? '-' }}
            />
          </InfoCardItem>
        </InfoCardGroup>
        <InfoCardGroupFlex>
          <InfoCardGroupFlexInner>
            <InfoCardItem>
              <Text $type="smallBold" $color="gray">
                Created At
              </Text>
              <Text $type="caption">
                {DateTime.fromISO(project.createdAt).toFormat(
                  'yyyy-LL-dd · HH:mm',
                )}
              </Text>
            </InfoCardItem>
            <InfoCardItem>
              <Text $type="smallBold" $color="gray">
                Total Spendings
              </Text>
              <Text $type="caption">Rp {project.totalSpendings!}</Text>
            </InfoCardItem>
            <InfoCardItem>
              <Text $type="smallBold" $color="gray">
                Status
              </Text>
              <StatusLabel $type="caption">
                <Dot
                  $color={
                    project.status === BPTestingProjectStatus.ACTIVE
                      ? 'success'
                      : 'error'
                  }
                />
                {statusLabel}
              </StatusLabel>
            </InfoCardItem>
          </InfoCardGroupFlexInner>
        </InfoCardGroupFlex>
      </InfoCardGroupsWrapper>
    </InfoCardBox>
  );
};

export default InfoCard;
